import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Formik } from "formik";
import { useSnackbar } from "notistack";
import useLoader from "../../../../hooks/useLoader";
import { getErrorMessage } from "../../../../helpers/functions";
import FormField from "../../FormField/FormField";
import { PrimaryCTAButton } from "../../Buttons";
import {
  CustomCheckbox,
  CustomInputField,
  CustomRadioGroup,
  CustomRadio,
} from "../../FormInputs";
import CaseService from "../../../../services/CaseService";
import _ from "lodash";
import { encrypt, renderFeeKind } from "../../../../helpers/functions";
import { PAYMENT_STATUS } from "../../../../helpers/constants";
import {
  refundScheme,
  GenerateInvoiceValidationSchema,
} from "../../../../helpers/validationSchema";
import { FieldArray, Form } from "formik";
import NumberFormat from "react-number-format";
import { Fragment } from "react";
import COLORS from "../../../../assets/Colors";
import theme from "../../../../assets/theme";
import {
  NoteContainer,
  SectionHeader,
  SectionTitle,
  PaymentAmount,
  PaymentCard,
  PaymentCardFooter,
  PaymentCardHeader,
  PaymentCount,
  PaymentInfo,
  PaymentsList,
  RefundOptionsContainer,
  RefundOptionsHeader,
  RadioGroupWrapper,
  RefundedAmount,
  RefundedBadge,
  RefundedText,
  FeeLabel,
  AgentRole,
  AmountInputWrapper,
  AmountValue,
  InputContainer,
  InputLabel,
  HelperText,
  FullRefundDisplay,
  FullRefundValue,
  CommentsSection,
  CommentLabel,
  ButtonContainer,
  CheckIcon,
  CheckboxLabel,
  CheckboxWrapper,
} from "../../RefundModal";
import { Strip, TabBtn, TabLabel } from "./CaseTabStrip";

export default function InvoiceAndRefundPanel({
  open,
  setOpen,
  caseId,
  id = "",
  heading = "Invoice & Refund",
  viewOnly,
}) {
  const [activeTab, setActiveTab] = useState("invoice");
  const [paymentHistory, setPaymentHistory] = useState([]);

  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();

  useEffect(() => {
    async function getPaymentHistory() {
      try {
        const response = await CaseService.getPayments(id);
        if (response?.data) setPaymentHistory(response.data);
      } catch (error) {
        console.error(error);
      }
    }
    if (open && activeTab === "refund" && id) getPaymentHistory();
  }, [id, open, activeTab]);

  if (!open) return null;

  const onGenerateInvoiceSubmit = async (values) => {
    try {
      setLoader({ state: true, message: "Generating invoice..." });
      const response = await CaseService.generateInvoiceCase(caseId, {
        ...values,
        amount: Number(values.amount?.toString()?.split(",")?.join("")),
      });
      enqueueSnackbar(response?.message || "Invoice generated successfully", {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error), { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  const onRefundSubmit = async (values) => {
    try {
      setLoader({ state: true, message: "Submitting refund..." });
      const payload = {
        refundComment: values?.comment,
        refunds: values?.payments
          ?.filter((p) => p.checked)
          .map((el) => ({
            id: el?.id,
            value:
              el?.type === "full"
                ? encrypt(
                    Number(
                      el?.cartItem?.fee?.discPrice
                        ?.toString()
                        ?.split(",")
                        ?.join(""),
                    ),
                  )
                : encrypt(Number(el?.amount?.toString()?.split(",")?.join(""))),
          })),
      };
      const res = await CaseService.refundPayment(payload);
      if (res?.message) enqueueSnackbar(res.message, { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Unable to process Refund", { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  };

  return (
    <Container>
      <Content>
        <Strip>
          {[
            { label: "Generate Invoice", id: "invoice" },
            { label: "Process Refund", id: "refund" },
          ].map((tab) => (
            <TabBtn
              key={tab.id}
              $active={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              <TabLabel>{tab.label}</TabLabel>
            </TabBtn>
          ))}
        </Strip>
        {/* INVOICE TAB */}
        {activeTab === "invoice" && (
          <Formik
            initialValues={{
              feeType: "",
              amount: 0,
              claimantPercentage: "",
              incluseGst: false,
            }}
            validationSchema={GenerateInvoiceValidationSchema}
            onSubmit={onGenerateInvoiceSubmit}
          >
            {({
              values,
              handleChange,
              errors,
              touched,
              handleSubmit,
              setFieldValue,
            }) => (
              <Form onSubmit={handleSubmit}>
                <FormBody>
                  <FormSection>
                    <SectionLabel>
                      <SectionLabelBar />
                      Invoice details
                    </SectionLabel>
                    <FieldsStack>
                      {[
                        {
                          name: "feeType",
                          type: "text",
                          label: "Fees Type",
                          required: true,
                        },
                        {
                          name: "amount",
                          type: "numberFormat",
                          label: "Amount",
                          required: true,
                        },
                      ].map((input, index) => (
                        <FormField
                          key={index}
                          input={input}
                          values={values}
                          handleChange={handleChange}
                          touched={touched}
                          errors={errors}
                        />
                      ))}
                    </FieldsStack>
                  </FormSection>

                  <FormSection>
                    <SectionLabel>
                      <SectionLabelBar />
                      Payment distribution
                    </SectionLabel>
                    <FieldsStack>
                      <FormField
                        input={{
                          name: "claimantPercentage",
                          type: "number",
                          label: "Claimant Percentage",
                          required: true,
                        }}
                        values={values}
                        handleChange={handleChange}
                        touched={touched}
                        errors={errors}
                      />
                    </FieldsStack>
                  </FormSection>

                  <FormSection>
                    <SectionLabel>
                      <SectionLabelBar />
                      Tax options
                    </SectionLabel>
                    <GSTBox>
                      <CustomRadio
                        radioInputs={[
                          { label: "Include GST", value: "incluseGst" },
                        ]}
                        name="incluseGst"
                        values={values}
                        setFieldValue={setFieldValue}
                      />
                      <GSTNote>GST will be added to the invoice amount</GSTNote>
                    </GSTBox>
                  </FormSection>
                </FormBody>

                <FormFooter>
                  <PrimaryCTAButton
                    type="submit"
                    style={{ width: "auto" }}
                    disabled={viewOnly}
                  >
                    Generate Invoice
                  </PrimaryCTAButton>
                </FormFooter>
              </Form>
            )}
          </Formik>
        )}

        {/* REFUND TAB */}
        {activeTab === "refund" && (
          <Formik
            initialValues={{
              payments: paymentHistory?.map((el) => ({
                ...el,
                amount: el?.cartItem?.fee?.discPrice,
                type: "full",
                checked: false,
              })),
              comment: "",
            }}
            validationSchema={refundScheme}
            enableReinitialize
            onSubmit={onRefundSubmit}
          >
            {({
              values,
              handleChange,
              errors,
              touched,
              handleSubmit,
              setFieldValue,
              isValid,
              setFieldTouched,
            }) => (
              <Form onSubmit={handleSubmit}>
                <ContentWrapper>
                  <NoteContainer>
                    <SectionHeader>
                      <SectionTitle>Payment History</SectionTitle>
                      {values?.payments?.length > 0 && (
                        <PaymentCount>
                          {values.payments.length} payment
                          {values.payments.length !== 1 ? "s" : ""}
                        </PaymentCount>
                      )}
                    </SectionHeader>

                    <PaymentsList>
                      <FieldArray
                        name="payments"
                        render={() => (
                          <Fragment>
                            {values?.payments?.map((payment, index) => (
                              <Fragment key={index}>
                                <PaymentCard
                                  isRefunded={!!payment?.refundedAt}
                                  isDisabled={
                                    !Number(
                                      payment?.cartItem?.fee?.discPrice,
                                    ) ||
                                    !Number(payment?.allowedRefund?.totalRefund)
                                  }
                                >
                                  <PaymentCardHeader>
                                    <PaymentInfo>
                                      <FeeLabel>
                                        {renderFeeKind(
                                          PAYMENT_STATUS[
                                            payment?.cartItem?.feeKind
                                          ],
                                          payment?.case,
                                        )}{" "}
                                        Fee
                                      </FeeLabel>
                                      <AgentRole>
                                        {_.startCase(
                                          payment?.cartItem?.agentRole,
                                        )}
                                      </AgentRole>
                                    </PaymentInfo>
                                    <PaymentAmount>
                                      <AmountValue>
                                        {payment?.cartItem?.fee?.unit}{" "}
                                        {Number(
                                          payment?.cartItem?.fee?.discPrice,
                                        ).toFixed(2)}
                                      </AmountValue>
                                    </PaymentAmount>
                                  </PaymentCardHeader>

                                  <PaymentCardFooter>
                                    {payment?.refundedAt ? (
                                      <RefundedBadge>
                                        <CheckIcon>✓</CheckIcon>
                                        <div>
                                          <RefundedText>Refunded</RefundedText>
                                          <RefundedAmount>
                                            {payment?.cartItem?.fee?.unit}{" "}
                                            {Number(
                                              payment?.refundAmount,
                                            ).toFixed(2)}
                                          </RefundedAmount>
                                        </div>
                                      </RefundedBadge>
                                    ) : (
                                      <CheckboxWrapper
                                        disabled={
                                          !Number(
                                            payment?.cartItem?.fee?.discPrice,
                                          ) ||
                                          !Number(
                                            payment?.allowedRefund?.totalRefund,
                                          )
                                        }
                                      >
                                        <CustomCheckbox
                                          id={`refund-${index}`}
                                          name={`payments[${index}].checked`}
                                          checked={
                                            values?.payments[index]?.checked
                                          }
                                          disabled={
                                            !Number(
                                              payment?.cartItem?.fee?.discPrice,
                                            ) ||
                                            !Number(
                                              payment?.allowedRefund
                                                ?.totalRefund,
                                            )
                                          }
                                          onChange={(e) => {
                                            setFieldValue(
                                              `payments[${index}].type`,
                                              "full",
                                            );
                                            handleChange(e);
                                          }}
                                        />
                                        <CheckboxLabel>
                                          Select for refund
                                        </CheckboxLabel>
                                      </CheckboxWrapper>
                                    )}
                                  </PaymentCardFooter>

                                  {payment.checked && (
                                    <RefundOptionsContainer>
                                      <RefundOptionsHeader>
                                        Refund Options
                                      </RefundOptionsHeader>
                                      <RadioGroupWrapper>
                                        <CustomRadioGroup
                                          radioInputs={[
                                            {
                                              label: "Full Refund",
                                              value: "full",
                                            },
                                            {
                                              label: "Partial Refund",
                                              value: "partial",
                                            },
                                          ]}
                                          value={values.payments[index].type}
                                          name={`payments[${index}].type`}
                                          {...{ values, handleChange }}
                                        />
                                      </RadioGroupWrapper>
                                      <AmountInputWrapper>
                                        {values?.payments[index]?.type ===
                                        "partial" ? (
                                          <InputContainer>
                                            <InputLabel>
                                              Refund Amount
                                            </InputLabel>
                                            <NumberFormat
                                              thousandSeparator
                                              thousandsGroupStyle="lakh"
                                              isNumericString={true}
                                              decimalScale={2}
                                              fixedDecimalScale
                                              allowNegative={false}
                                              className={"input-white"}
                                              displayType={"input"}
                                              value={
                                                values.payments[index].amount
                                              }
                                              type={"text"}
                                              customInput={CustomInputField}
                                              name={`payments[${index}].amount`}
                                              onChange={handleChange}
                                              variant="outlined"
                                              error={
                                                errors?.payments?.[index]
                                                  ?.amount &&
                                                touched?.payments?.[index]
                                                  ?.amount
                                              }
                                              onBlur={() =>
                                                setFieldTouched(
                                                  `payments[${index}].amount`,
                                                  true,
                                                )
                                              }
                                            />
                                            {errors?.payments?.[index]
                                              ?.amount &&
                                              touched?.payments?.[index]
                                                ?.amount && (
                                                <HelperText>
                                                  {
                                                    errors?.payments?.[index]
                                                      ?.amount
                                                  }
                                                </HelperText>
                                              )}
                                          </InputContainer>
                                        ) : (
                                          <FullRefundDisplay>
                                            <InputLabel>
                                              Refund Amount
                                            </InputLabel>
                                            <FullRefundValue>
                                              {payment?.cartItem?.fee?.unit}{" "}
                                              {Number(
                                                payment?.cartItem?.fee
                                                  ?.discPrice,
                                              ).toFixed(2)}
                                            </FullRefundValue>
                                          </FullRefundDisplay>
                                        )}
                                      </AmountInputWrapper>
                                    </RefundOptionsContainer>
                                  )}
                                </PaymentCard>
                              </Fragment>
                            ))}
                          </Fragment>
                        )}
                      />
                    </PaymentsList>

                    {values?.payments?.every(
                      (el) => el.refundedAt !== null,
                    ) ? null : (
                      <CommentsSection>
                        <CommentHeader>
                          <CommentLabel>Add Comments (Optional)</CommentLabel>
                        </CommentHeader>
                        <CustomInputField
                          variant="outlined"
                          className={"input-height-auto"}
                          multiline={true}
                          rows={3}
                          maxRows={3}
                          placeholder="Add any additional notes or reasons for the refund..."
                          name={"comment"}
                          value={values?.comment}
                          onChange={handleChange}
                        />
                      </CommentsSection>
                    )}
                  </NoteContainer>

                  <ButtonContainer>
                    <PrimaryCTAButton
                      type="submit"
                      style={{ width: "auto" }}
                      disabled={
                        !isValid ||
                        !values.payments?.some((el) => el.checked) ||
                        viewOnly
                      }
                    >
                      Process Refund
                    </PrimaryCTAButton>
                  </ButtonContainer>
                </ContentWrapper>
              </Form>
            )}
          </Formik>
        )}
      </Content>
    </Container>
  );
}
const ACCENT = "#0E7490";

const Container = styled.div`
  width: 100%;
  padding: 12px 0;

  @media (max-width: 768px) {
    padding: 8px 0;
  }
`;

const Content = styled.div`
  background: #ffffff;
  border: 1px solid #e6e8ec;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  overflow: hidden;
`;

const FormBody = styled.div`
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 22px;

  @media (max-width: 768px) {
    padding: 16px 18px;
    gap: 16px;
  }
`;

const FormSection = styled.div``;

const SectionLabelBar = styled.span`
  width: 3px;
  height: 12px;
  border-radius: 2px;
  background: ${ACCENT};
  display: inline-block;
`;

const SectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  letter-spacing: -0.005em;
  color: #667085;
  margin-bottom: 10px;
`;

const FieldsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const GSTBox = styled.div`
  background: #f7f8fa;
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #e6e8ec;
`;

const GSTNote = styled.p`
  margin: 8px 0 0 28px;
  font-size: 11.5px;
  font-family: ${theme.fonts.primaryFontItalic};
  color: ${COLORS.INPUT_LABEL};
`;

const FormFooter = styled.div`
  padding: 16px 28px 20px;
  border-top: 1px solid #e6e8ec;
  background: #f7f8fa;
  display: flex;
  justify-content: flex-end;

  button {
    border-radius: 9px !important;
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease,
      filter 0.15s ease;

    &:hover:not(:disabled) {
      transform: translateY(-1px);
      filter: brightness(1.03);
      box-shadow: 0 8px 18px rgba(14, 116, 144, 0.22);
    }
  }

  @media (max-width: 768px) {
    padding: 14px 18px 18px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;
